import type { MouseEvent } from "react";
import type { Platform } from "../platform/Platform";
import { targetForUrl } from "../platform/targets";

/**
 * A modified click on an in-app link (`a[href]`) in a host with windows of its own (the
 * desktop): Cmd/Ctrl or the middle button opens the route in a new workspace tab, Shift in a new
 * window. WebView2's opener would send it to the system browser instead. A plain click, a
 * download, a link to another site, a click already handled, and a host without windows (the
 * web, where the browser opens the link itself) are left alone.
 */
export function openModifiedLinkInWorkspace(platform: Platform, event: MouseEvent): void {
    if (!platform.windows || event.defaultPrevented || event.button > 1) return;
    const anchor = (event.target as Element).closest?.("a[href]");
    if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download")) return;
    const newTab =
        event.button === 1 || event.ctrlKey || event.metaKey || anchor.target === "_blank";
    if (!newTab && !event.shiftKey) return;
    const url = new URL(anchor.href);
    if (url.origin !== window.location.origin) return;
    // App routes belong to workspace tabs and windows; ordinary clicks stay with the router.
    event.preventDefault();
    event.stopPropagation();
    platform.open(targetForUrl(`${url.pathname}${url.search}${url.hash}`), {
        newTab,
        newWindow: event.shiftKey && !newTab,
    });
}

/** A click the browser (or `openModifiedLinkInWorkspace`) opens elsewhere: not the plain one a
 *  link handles in place. */
export function isModifiedLinkClick(event: MouseEvent): boolean {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
}
